// Main application router for multi-tenant platform
// Requirements: 5.3, 5.4

import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { ControlPanel } from './ControlPanel';
import { MarketingHomepage } from './Marketing/MarketingHomepage';
import { AuthenticationPage } from './Authentication/AuthenticationPage';
import './AppRouter.css';

type AppView = 'marketing' | 'auth' | 'control-panel';

const AppRouterContent: React.FC = () => {
  const { state: authState, logout } = useAuth();
  const [currentView, setCurrentView] = useState<AppView>('marketing');
  const [isLoading, setIsLoading] = useState(true);

  // Initialize app state based on authentication
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Use auth context state instead of AuthService
        if (authState.isAuthenticated && authState.user) {
          setCurrentView('control-panel');
        } else {
          setCurrentView('marketing');
        }
      } catch (error) {
        console.error('App initialization error:', error);
        setCurrentView('marketing');
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, [authState.isAuthenticated, authState.user]);

  // Update view based on authentication state changes
  useEffect(() => {
    console.log('Auth state changed:', { 
      isAuthenticated: authState.isAuthenticated, 
      hasUser: !!authState.user,
      currentView,
      userEmail: authState.user?.email 
    });
    
    if (authState.isAuthenticated && authState.user) {
      console.log('Redirecting to control panel');
      setCurrentView('control-panel');
    } else if (!authState.isAuthenticated && currentView === 'control-panel') {
      console.log('Redirecting to marketing');
      setCurrentView('marketing');
    }
  }, [authState.isAuthenticated, authState.user, currentView]);

  const handleNavigateToAuth = (mode: 'login' | 'register' = 'login') => {
    setCurrentView('auth');
  };

  const handleAuthSuccess = (user: any) => {
    console.log('handleAuthSuccess called with user:', user);
    console.log('Current auth state:', authState);
    // Don't manually set the view here - let the auth state effect handle it
    // The auth context should already be updated by the login process
  };

  const handleLogout = async () => {
    try {
      await logout();
      setCurrentView('marketing');
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation to marketing even if logout fails
      setCurrentView('marketing');
    }
  };

  const handleNavigateToMarketing = () => {
    setCurrentView('marketing');
  };

  if (isLoading) {
    return (
      <div className="app-router__loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading Optinode AI...</p>
        </div>
      </div>
    );
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'marketing':
        return (
          <MarketingHomepage
            onLogin={() => handleNavigateToAuth('login')}
            onGetStarted={() => handleNavigateToAuth('register')}
            onNavigateHome={handleNavigateToMarketing}
          />
        );

      case 'auth':
        return (
          <AuthenticationPage
            onAuthSuccess={handleAuthSuccess}
            initialMode="login"
          />
        );

      case 'control-panel':
        return (
          <ControlPanel
            onLogout={handleLogout}
            onNavigateHome={handleNavigateToMarketing}
          />
        );

      default:
        return (
          <MarketingHomepage
            onLogin={() => handleNavigateToAuth('login')}
            onGetStarted={() => handleNavigateToAuth('register')}
            onNavigateHome={handleNavigateToMarketing}
          />
        );
    }
  };

  return (
    <div className={`app-router app-view-${currentView}`}>
      {renderCurrentView()}
    </div>
  );
};

export const AppRouter: React.FC = () => {
  return (
    <AuthProvider>
      <AppRouterContent />
    </AuthProvider>
  );
};

export default AppRouter;